package it.neckar.lizergy.model.http

import it.neckar.lizergy.model.configuration.PhotovoltaicsConfiguration.PhotovoltaicsConfigurationId
import it.neckar.lizergy.model.configuration.quote.QuoteSnapshot.QuoteSnapshotId
import it.neckar.lizergy.model.project.ProjectConfiguration.PhotovoltaicsProjectId
import it.neckar.open.annotations.Effective
import it.neckar.open.http.Url
import it.neckar.open.http.UrlPattern
import it.neckar.uuid.resolve

/**
 * PDF endpoints interface
 *
 * Note: @Effective annotations on properties use relative paths that will be combined with baseUrl
 */
interface PdfEndpoints : Endpoints {
  /**
   * URL to projects collection
   */
  @Effective("projects")
  val Projects: UrlPattern.Relative0
    get() = baseUrl + "projects"


  @Effective("projects/{projectId}")
  val Project: UrlPattern.Relative1
    get() = Projects + EndpointsParameters.projectId

  fun projectUrl(projectId: PhotovoltaicsProjectId): Url.Relative = Project.resolve(projectId)


  @Effective("projects/{projectId}/configurations")
  val Configurations: UrlPattern.Relative1
    get() = Project + "configurations"

  fun configurationsUrl(projectId: PhotovoltaicsProjectId): Url.Relative = Configurations.resolve(projectId)


  @Effective("projects/{projectId}/configurations/{configurationId}")
  val Configuration: UrlPattern.Relative2
    get() = Configurations + EndpointsParameters.configurationId

  /**
   * Creates the URL to a quote configuration
   */
  fun configurationUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = Configuration.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots")
  val QuoteSnapshots: UrlPattern.Relative2
    get() = Configuration + "quote-snapshots"

  fun quoteSnapshotsUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = QuoteSnapshots.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots/current")
  val CurrentQuoteSnapshot: UrlPattern.Relative2
    get() = QuoteSnapshots + "current"

  fun currentQuoteSnapshotUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = CurrentQuoteSnapshot.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots/{quoteSnapshotId}")
  val QuoteSnapshot: UrlPattern.Relative3
    get() = QuoteSnapshots + EndpointsParameters.quoteSnapshotId

  fun quoteSnapshotUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Relative = QuoteSnapshot.resolve(projectId, configurationId, quoteSnapshotId)


  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots/current/offer")
  val QuoteOfferForCurrent: UrlPattern.Relative2
    get() = CurrentQuoteSnapshot + "offer"

  fun quoteOfferForCurrentUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = QuoteOfferForCurrent.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots/{quoteSnapshotId}/offer")
  val QuoteOfferForSnapshot: UrlPattern.Relative3
    get() = QuoteSnapshot + "offer"

  fun quoteOfferForSnapshotUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Relative = QuoteOfferForSnapshot.resolve(projectId, configurationId, quoteSnapshotId)


  @Effective("projects/{projectId}/configurations/{configurationId}/quote-snapshots/{quoteSnapshotId}/order-confirmation")
  val OrderConfirmationForSnapshot: UrlPattern.Relative3
    get() = QuoteSnapshot + "order-confirmation"

  fun orderConfirmationForSnapshotUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Relative = OrderConfirmationForSnapshot.resolve(projectId, configurationId, quoteSnapshotId)


  @Effective("projects/{projectId}/configurations/{configurationId}/string-plan")
  val StringPlan: UrlPattern.Relative2
    get() = Configuration + "string-plan"

  fun stringPlanUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = StringPlan.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/assembly-portfolio")
  val AssemblyPortfolio: UrlPattern.Relative2
    get() = Configuration + "assembly-portfolio"

  fun assemblyPortfolioUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = AssemblyPortfolio.resolve(projectId, configurationId)

  @Effective("projects/{projectId}/configurations/{configurationId}/assembly-portfolio-roof")
  val AssemblyPortfolioRoof: UrlPattern.Relative2
    get() = Configuration + "assembly-portfolio-roof"

  fun assemblyPortfolioRoofUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = AssemblyPortfolioRoof.resolve(projectId, configurationId)

  @Effective("projects/{projectId}/configurations/{configurationId}/assembly-portfolio-basement")
  val AssemblyPortfolioBasement: UrlPattern.Relative2
    get() = Configuration + "assembly-portfolio-basement"

  fun assemblyPortfolioBasementUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = AssemblyPortfolioBasement.resolve(projectId, configurationId)


  @Effective("projects/{projectId}/configurations/{configurationId}/documentation")
  val Documentation: UrlPattern.Relative2
    get() = Configuration + "documentation"

  fun documentationUrl(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Relative = Documentation.resolve(projectId, configurationId)
}
